/**
 * taminzo-search.js — custom storefront search (single central file).
 *
 * Requires Globo "Search" turned OFF (Filters stay on). Then:
 *   1) DROPDOWN — native Dawn predictive fetches `/search/suggest?…&section_id=predictive-search`
 *      (HTML). We intercept that fetch, call our typo-tolerant API, and return HTML in the exact
 *      section shape Dawn injects — with our products + a real Brands group, native predictive CSS.
 *   2) RESULTS PAGE (/search?q=…) — replaces the theme's product grid with our paginated,
 *      typo-tolerant, mapping-aware results (needs Globo filters off on the search template).
 *
 * SAFETY — ENABLED=false → does nothing (native works). PREVIEW_ONLY=true → only ?tzsearch=1
 * sessions get our search. Every call has a timeout + circuit breaker → Railway down = native.
 * Fallback on the dropdown is literally the original fetch (native predictive search).
 */
/* build 2026-07-09b — cache-bust to force a fresh asset version (prior ?v was served as text/html) */
(function () {
  'use strict';

  var API = 'https://sellers-be.taminzo.com/api';
  // Market for pricing/uplift: theme sets window.taminzoConfig.country = localization.country.iso_code on
  // every page (reels-only globals kept as fallback). Without this, search sent market=PK → no uplift.
  var COUNTRY = (window.taminzoConfig && window.taminzoConfig.country) || window.__TAMINZO_COUNTRY__ || window.__REELS_COUNTRY__ || 'PK';
  // Timeouts sized for slow mobile connections — too short (1.5s) aborted legitimate responses and
  // dropped those shoppers to native/Globo. 5s for the dropdown and the (heavier) results/collection fetch.
  // PAGE_SIZE 20: each result is a Shopify Section-Rendering fetch (one per product, for real F&F
  // pricing) — 20 keeps the per-page parallel-request count reasonable while making each page tall
  // enough that the next-page trigger doesn't fire from a tiny scroll. Infinite scroll loads more.
  var TIMEOUT_MS = 5000, RESULTS_TIMEOUT_MS = 5000, BEACON_DEBOUNCE_MS = 1200, PAGE_SIZE = 20;

  var ENABLED = false;        // master switch (OFF = native)
  var PREVIEW_ONLY = false;  // LIVE for all shoppers
  var FILTERS_ENABLED = true; // storefront filter bar on the results page (OFF = results with no filters)
  var IMAGE_SEARCH_ENABLED = true; // visual/image search: camera button + ?img= results mode (OFF = hidden, no img searches)
  // COLLECTION-PAGE takeover — INDEPENDENT of search (search is already live/proven). OFF here → collection
  // pages fall back to native Globo/Shopify, search untouched. For an INSTANT prod kill without a theme push,
  // the pre-paint hide + window.taminzoCollection are ALSO gated by a Shopify theme setting in theme.liquid;
  // unchecking it in the customizer reverts collections to native immediately (see theme.liquid).
  var COLLECTION_ENABLED = false;

  var CB_FAILS_KEY = 'tz_search_cb_fails', CB_UNTIL_KEY = 'tz_search_cb_until';
  var CB_MAX_FAILS = 2, CB_COOLDOWN_MS = 60000;
  var PREDICTIVE_URL = (window.routes && window.routes.predictive_search_url) || '/search/suggest';

  function isPreview() {
    if (/[?&]tzsearch=1/.test(location.search)) { try { localStorage.setItem('tzsearch', '1'); } catch (e) {} return true; }
    try { return localStorage.getItem('tzsearch') === '1'; } catch (e) { return false; }
  }
  var ACTIVE = ENABLED && (!PREVIEW_ONLY || isPreview());
  if (!ACTIVE) return; // flag off / not preview → native + Globo untouched

  var origFetch = (window.fetch || function () { return Promise.reject(); }).bind(window);
  // The theme's STANDARD loader (rotator + dash SVG, used site-wide) — one source of truth so every
  // search-page spinner (initial load, load-more, image-search overlay) matches the rest of the store.
  var SPIN = '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';

  // ── Circuit breaker ───────────────────────────────────────────────────────
  function cbTripped() { try { return parseInt(sessionStorage.getItem(CB_UNTIL_KEY) || '0', 10) > Date.now(); } catch (e) { return false; } }
  function cbFail() {
    try {
      var n = parseInt(sessionStorage.getItem(CB_FAILS_KEY) || '0', 10) + 1;
      sessionStorage.setItem(CB_FAILS_KEY, String(n));
      if (n >= CB_MAX_FAILS) sessionStorage.setItem(CB_UNTIL_KEY, String(Date.now() + CB_COOLDOWN_MS));
    } catch (e) {}
  }
  function cbReset() { try { sessionStorage.removeItem(CB_FAILS_KEY); sessionStorage.removeItem(CB_UNTIL_KEY); } catch (e) {} }

  // ── Price — verbatim reels logic (base PKR × Shopify.currency.rate) ──────────
  function formatPrice(p) {
    if (p == null) return '';
    var active = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'PKR';
    var rate = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1);
    var converted = p * rate;
    if (active === 'PKR') return 'PKR ' + Math.round(converted).toLocaleString('en-PK');
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: active, maximumFractionDigits: 2 }).format(converted); }
    catch (e) { return active + ' ' + converted.toFixed(2); }
  }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'); }

  // Central fetch. Normally GET, but auto-switches to POST when the URL would get too long (many filters
  // selected) — the backend exposes GET+POST twins of /results with identical logic, so the shopper NEVER
  // fails on a long URL. Image search rides the same helper (its ?img={id} is short, but its filter combos
  // can still grow). Only the querystring becomes the JSON body; the path (endpoint) is unchanged.
  var MAX_URL = 1900; // conservative ceiling (real limit ~2048); beyond this → POST
  function qsToBody(qs) {
    var reserved = { q: 1, img: 1, handle: 1, market: 1, page: 1, limit: 1, priceMin: 1, priceMax: 1, onSale: 1, sort: 1 };
    var body = {}, filters = {};
    new URLSearchParams(qs).forEach(function (v, k) {
      if (reserved[k]) {
        if (k === 'page' || k === 'limit') body[k] = parseInt(v, 10) || 0;
        else if (k === 'priceMin' || k === 'priceMax') body[k] = parseFloat(v);
        else if (k === 'onSale') body[k] = (v === 'true');
        else body[k] = v;                                  // q / img / market / sort — bound case-insensitively
      } else if (v) {
        filters[k] = v.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
      }
    });
    if (Object.keys(filters).length) body.filters = filters;
    return body;
  }
  function apiJson(path, timeout) {
    var ctrl = new AbortController();
    var t = setTimeout(function () { ctrl.abort(); }, timeout || TIMEOUT_MS);
    var url = API + path, opts = { signal: ctrl.signal, mode: 'cors', credentials: 'omit' };
    var qi = path.indexOf('?');
    if (url.length > MAX_URL && qi !== -1) {
      url = API + path.slice(0, qi);   // same endpoint, POST twin
      opts.method = 'POST';
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(qsToBody(path.slice(qi + 1)));
    }
    return origFetch(url, opts)
      .then(function (r) { clearTimeout(t); if (!r.ok) throw new Error('http ' + r.status); return r.json(); });
  }
  function anonId() { try { return localStorage.getItem('taminzo_stable_id') || localStorage.getItem('reels_uid') || ''; } catch (e) { return ''; } }
  function beacon(body) {
    try { origFetch(API + '/storefront/search/event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true, mode: 'cors', credentials: 'omit' }).catch(function () {}); } catch (e) {}
  }

  // ══ 1) DROPDOWN — intercept native predictive fetch, return our section HTML ══
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf(PREDICTIVE_URL) !== -1 && url.indexOf('section_id=predictive-search') !== -1 && !cbTripped()) {
        var m = url.match(/[?&]q=([^&]*)/);
        var term = m ? decodeURIComponent(m[1].replace(/\+/g, ' ')).trim() : '';
        if (term) {
          return apiJson('/storefront/search/suggest?q=' + encodeURIComponent(term) + '&market=' + encodeURIComponent(COUNTRY))
            .then(function (data) {
              cbReset(); scheduleBeacon(term, data);
              return new Response(sectionHtml(term, data), { status: 200, headers: { 'Content-Type': 'text/html' } });
            })
            .catch(function () { cbFail(); return origFetch(input, init); }); // fallback → native
        }
      }
    } catch (e) {}
    return origFetch(input, init);
  };

  function sectionHtml(term, data) {
    var res = (data && data.resources && data.resources.results) || {};
    var queries = res.queries || [], collections = res.collections || [], brands = res.brands || [], products = res.products || [];
    var id = 0, col1 = '';

    // Order: Brands → Collections → Suggestions (then Products in the group below).
    if (brands.length) {
      var bl = '';
      brands.forEach(function (b) { bl += li('brand', ++id, b.url, row(b.title, b.title, false)); });
      col1 += group('Brands', 'predictive-search-brands', 'ps-brands', bl);
    }
    if (collections.length) {
      var cl = '';
      collections.forEach(function (c) { cl += li('collection', ++id, c.url, row(c.title, c.title, false)); });
      col1 += group('Collections', 'predictive-search-collections', 'ps-collections', cl);
    }
    if (queries.length) {
      var list = '';
      queries.forEach(function (q) { list += li('query', ++id, q.url, row(q.styled_text || q.text, q.text, true)); });
      col1 += group('Suggestions', 'predictive-search-queries', 'ps-queries', list);
    }

    var groups = '';
    if (col1) groups += '<div class="predictive-search__result-group">' + col1 + '</div>';
    if (products.length) {
      var pl = '';
      products.forEach(function (p, i) { pl += productLi(p, ++id, i + 1); });
      groups += '<div class="predictive-search__result-group"><div>' +
        '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' +
        '<ul id="ps-products" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">' + pl + '</ul></div></div>';
    }

    var total = queries.length + collections.length + brands.length + products.length;
    var wrapCls = 'predictive-search__results-groups-wrapper' + (products.length ? '' : ' predictive-search__results-groups-wrapper--no-products') +
      (col1 ? '' : ' predictive-search__results-groups-wrapper--no-suggestions');

    var inner = '<div id="predictive-search-results" role="listbox">' +
      '<button type="button" class="tz-ps-close" aria-label="Close suggestions">&times;</button>' +
      (total ? '<div id="predictive-search-results-groups-wrapper" class="' + wrapCls + '">' + groups + '</div>' : '') +
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
        '<a href="/search?q=' + encodeURIComponent(term) + '" class="predictive-search__item predictive-search__item--term link link--text h5 tz-view-all" tabindex="-1" role="option" aria-selected="false">' +
        '<span data-predictive-search-search-for-text>View all results for &ldquo;' + esc(term) + '&rdquo;</span>' +
        '<span class="tz-view-all-arrow" aria-hidden="true">&rarr;</span></a></div></div>' +
      '<span class="hidden" data-predictive-search-live-region-count-value>' + (total === 0 ? 'No results for &ldquo;' + esc(term) + '&rdquo;' : total + ' results') + '</span>';
    return '<div id="shopify-section-predictive-search">' + inner + '</div>';
  }
  function group(h, hid, lid, items) {
    return '<div><h2 id="' + hid + '" class="predictive-search__heading text-body caption-with-letter-spacing">' + esc(h) + '</h2>' +
      '<ul id="' + lid + '" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="' + hid + '">' + items + '</ul></div>';
  }
  function li(kind, id, url, content) {
    return '<li id="predictive-search-option-' + kind + '-' + id + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
      '<a href="' + esc(url) + '" class="predictive-search__item link link--text" tabindex="-1">' + content + '</a></li>';
  }
  function row(styled, plain, isQuery) {
    return '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
      '<p class="predictive-search__item-heading h5' + (isQuery ? ' predictive-search__item-query-result' : '') + '" aria-label="' + esc(plain) + '">' + esc(styled) + '</p></div>';
  }
  function productLi(p, id, pos) {
    var img = p.image || (p.featured_image && p.featured_image.url) || '';
    var onSale = p.compare_at_price != null && p.compare_at_price > p.price;
    var price = '<div class="price' + (onSale ? ' price--on-sale' : '') + '"><div class="price__container">' +
      (onSale ? '<div class="price__sale"><span class="price-item price-item--regular"><s>' + esc(formatPrice(p.compare_at_price)) + '</s></span> <span class="price-item price-item--sale">' + esc(formatPrice(p.price)) + '</span></div>'
              : '<div class="price__regular"><span class="price-item price-item--regular">' + esc(formatPrice(p.price)) + '</span></div>') + '</div></div>';
    return '<li id="predictive-search-option-product-' + id + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
      '<a href="' + esc(p.url) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1" data-tz-product-id="' + esc(p.id) + '" data-tz-position="' + pos + '">' +
      (img ? '<img class="predictive-search__image" src="' + esc(imgUrl(img, 100)) + '" alt="' + esc(p.title) + '" width="50" height="50" loading="lazy" decoding="async">' : '') +
      '<div class="predictive-search__item-content">' +
        (p.vendor ? '<div class="predictive-search__item-vendor caption-with-letter-spacing">' + esc(p.vendor) + '</div>' : '') +
        '<p class="predictive-search__item-heading h5">' + esc(p.title) + '</p>' + price +
      '</div></a></li>';
  }

  var beaconTimer = null, lastTerm = '';
  function scheduleBeacon(term, data) {
    clearTimeout(beaconTimer);
    var res = (data && data.resources && data.resources.results) || {};
    var count = (res.products || []).length;
    beaconTimer = setTimeout(function () {
      if (term && term !== lastTerm) { lastTerm = term; beacon({ type: 'search', q: term, market: COUNTRY, resultCount: count, anonId: anonId() }); }
    }, BEACON_DEBOUNCE_MS);
  }

  // ══ 2) RESULTS PAGE — render OUR results into the search page (real /search?q= URL), matched to
  //        the collection page: count bar, theme card fonts, Judge.me stars, New badge, infinite
  //        scroll. Hides the theme/Globo grid. API down → theme's page left as-is. ══
  function imgUrl(u, w) { if (!u) return ''; return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w; }
  // Responsive srcset (Shopify CDN resizes + auto-negotiates WebP/AVIF), matching the card-renderer widths.
  function imgSrcset(u) { if (!u) return ''; return [180, 360, 540, 720, 900, 1080].map(function (w) { return imgUrl(u, w) + ' ' + w + 'w'; }).join(', '); }
  var CARD_SIZES = '(min-width:990px) 25vw, (min-width:750px) 33vw, 50vw'; // matches the 2/3/4-col grid
  var NEW_DAYS = 30;
  // Globo's exact quick-add "bag_plus" icon (copied from its card template).
  var QA_SVG = '<svg width="35px" height="35px" viewBox="0 0 22 22" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="#fff"><g stroke-width="0"></g><g stroke-linecap="round" stroke-linejoin="round"></g><g><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-340,-3159)" fill="#fff"><g transform="translate(56,160)"><path d="M306.000448,3018 C306.000448,3018.552 305.552448,3019 305.000448,3019 L304.000448,3019 C304.000448,3020 304.105448,3021 303.000448,3021 C301.907448,3021 302.000448,3020 302.000448,3019 C301.000448,3019 300.000448,3019.105 300.000448,3018 C300.000448,3016.907 301.000448,3017 302.000448,3017 C302.000448,3016 301.895448,3015 303.000448,3015 C304.104448,3015 304.000448,3016 304.000448,3017 C305.000448,3017 306.000448,3016.895 306.000448,3018 L306.000448,3018 Z M297.000448,3005 L291.000448,3005 L291.000448,3004 C291.000448,3002.346 292.346448,3001 294.000448,3001 C295.654448,3001 297.000448,3002 297.000448,3004 L297.000448,3005 Z M300.694448,3007.859 L301.488448,3013.418 C302.078448,3013.071 302.742448,3012.929 303.454448,3013.038 L302.551448,3006.717 C302.410448,3005.732 301.566448,3005 300.571448,3005 L299.000448,3005 L299.000448,3004 C299.000448,3001 296.757448,2999 294.000448,2999 C291.243448,2999 289.000448,3001.243 289.000448,3004 L289.000448,3005 L287.735448,3005 C286.739448,3005 285.590448,3005.732 285.449448,3006.717 L284.020448,3016.717 C283.848448,3017.922 284.783448,3019 286.000448,3019 L298.000448,3019 L298.000448,3017 L287.153448,3017 C286.545448,3017 286.077448,3016.461 286.163448,3015.859 L287.306448,3007.859 C287.377448,3007.366 287.799448,3007 288.296448,3007 L289.000448,3007 L289.000448,3009 C289.000448,3009.552 289.448448,3010 290.000448,3010 C290.552448,3010 291.000448,3009.552 291.000448,3009 L291.000448,3007 L297.000448,3007 L297.000448,3009 C297.000448,3009.552 297.448448,3010 298.000448,3010 C298.552448,3010 299.000448,3009.552 299.000448,3009 L299.000448,3007 L299.704448,3007 C300.201448,3007 300.623448,3007.366 300.694448,3007.859 L300.694448,3007.859 Z"></path></g></g></g></g></svg>';

  // Cancel the <head> safety timer that reveals native if our JS never runs (see theme.liquid).
  function tzClearRevealTimer() { try { if (window.__tzReveal) { clearTimeout(window.__tzReveal); window.__tzReveal = null; } } catch (e) {} }
  // Reveal native (drop our takeover) — used when we can't render (BE down / no query / no container).
  function tzReveal() { try { var c = document.documentElement.classList; c.remove('tz-results-active'); c.remove('tz-img-active'); } catch (e) {} tzClearRevealTimer(); }

  // Mode-driven results engine — shared by the SEARCH results page (/search?q= | ?img=) and the
  // COLLECTION page (/collections/<handle>). Pass `colCfg = { handle }` for collection mode; omit for search.
  // The two modes differ ONLY in: scope (filterBase), results endpoint, scroll key, the search beacon, and
  // the empty-state copy — everything else (shell, cards, facets, sort, infinite scroll, fallback) is shared.
  function initResultsPage(colCfg) {
    var isCollection = !!colCfg;
    var term, imgId = null, imageMode = false, container, filterBase, scrollKey;
    var ENDPOINT = null;   // filter-module + fallback endpoint; null = search default (/storefront/search/results)

    if (isCollection) {
      if (cbTripped()) { tzReveal(); return; }          // BE recently down → native (Globo/Shopify) grid
      container = document.querySelector('#ProductGridContainer') || document.querySelector('#product-grid');
      if (!container) { tzReveal(); return; }
      term = null;
      filterBase = 'handle=' + encodeURIComponent(colCfg.handle);   // backend reads `handle` on /collection/results
      ENDPOINT = '/storefront/collection/results';
      scrollKey = 'tz_scroll:col:' + colCfg.handle;
    } else {
      if (!/^\/search/.test(location.pathname)) return;
      var _p = new URLSearchParams(location.search);
      term = _p.get('q');
      imgId = _p.get('img');
      imageMode = !term && !!imgId && IMAGE_SEARCH_ENABLED;   // ?img={id} with no q → visual search results
      if (imgId && !IMAGE_SEARCH_ENABLED) { tzReveal(); return; } // feature off → hand a stale ?img link to native
      if ((!term && !imageMode) || cbTripped()) { tzReveal(); return; } // no query/image / BE recently down → native
      container = document.querySelector('#ProductGridContainer') || document.querySelector('#product-grid');
      if (!container && imageMode) {
        // ?img= has no `q`, so Shopify treats the search as "not performed" and renders NO grid container —
        // just the empty "Search" page. Build our own mount inside the search template and hide the native
        // empty header (the head script already hides it pre-paint via .tz-img-active).
        var tpl = document.querySelector('.template-search');
        if (tpl) {
          var nativeHdr = tpl.querySelector('.template-search__header');
          if (nativeHdr) nativeHdr.style.display = 'none';
          container = document.createElement('div'); container.id = 'ProductGridContainer';
          tpl.appendChild(container);
        }
      }
      if (!container) { tzReveal(); return; }
      // Scope for the filter module + fallback path: text uses `q=`, image uses `img={id}`. Everything
      // downstream (filters, facets, sort, pagination, cards) is identical between the two modes.
      filterBase = imageMode ? ('img=' + encodeURIComponent(imgId)) : ('q=' + encodeURIComponent(term));
      scrollKey = 'tz_scroll:' + (term || ('img:' + imgId));
    }

    // Scroll handling — our results load ASYNC, so the browser's own restoration lands on the still-empty
    // (short) page and jumps to its bottom. We take over: disable auto-restore; on refresh/back we keep
    // loading result pages until the page is tall enough to reach the saved scroll spot, THEN jump there
    // (collections get this free — cards are server-rendered). Saved per query in sessionStorage.
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
    var savedY = 0;
    try { savedY = parseInt(sessionStorage.getItem(scrollKey) || '0', 10) || 0; } catch (e) {}
    var restoring = savedY > 60;   // only worth restoring if they'd actually scrolled
    if (!restoring) window.scrollTo(0, 0);

    var st = { page: 0, totalPages: 1, total: 0, loading: false, done: false, grid: null, bar: null };
    // Honor ?page=N so the rendered grid starts at that page — matches the native server-rendered
    // pagination the crawler follows (each ?page=N = distinct products), and avoids every ?page=N
    // rendering identical page-1 content (a duplicate-content trap for JS-rendering crawlers). Default = 1.
    // Users infinite-scroll onward from the landing page; scroll-restore is skipped when starting mid-series.
    var startPage = parseInt((new URLSearchParams(location.search)).get('page') || '1', 10) || 1;
    if (startPage > 1) { st.page = startPage - 1; restoring = false; }   // first fetchPage() → next = startPage
    // "First fetch" is tracked explicitly (NOT st.page === 0) because ?page=N starts st.page mid-series —
    // the first-fetch-only work (cancel native-reveal timer, feed facets, empty-check) must still run then.
    var firstFetch = true;

    // Persist the scroll spot (throttled while scrolling + flushed on unload) so refresh/back can restore.
    var saveTimer = null;
    function persistScroll() {
      var y = window.scrollY || window.pageYOffset || 0;
      try { if (y > 60) sessionStorage.setItem(scrollKey, String(y)); } catch (e) {}
    }
    function saveScroll() {
      if (restoring || saveTimer) return;   // don't overwrite the target while we're restoring
      saveTimer = setTimeout(function () { saveTimer = null; persistScroll(); }, 200);
    }
    window.addEventListener('scroll', saveScroll, { passive: true });
    window.addEventListener('pagehide', persistScroll);
    window.addEventListener('beforeunload', persistScroll);

    function fetchPage() {
      if (st.loading || st.done) return;
      st.loading = true;
      var next = st.page + 1;
      if (!firstFetch && !restoring) showMoreSpinner(); // bottom spinner for infinite-scroll pages (not the first, not restore)
      // The filter module builds the scoped URL (base q= + market + page + selected filters); without it,
      // fall back to the plain query URL. Either way the search query is always in the request.
      var path = st.filters
        ? st.filters.resultsPath(next, PAGE_SIZE)
        : (ENDPOINT || '/storefront/search/results') + '?' + filterBase + '&market=' + encodeURIComponent(COUNTRY) + '&page=' + next + '&limit=' + PAGE_SIZE;
      apiJson(path, RESULTS_TIMEOUT_MS)
        .then(function (data) {
          cbReset();
          // Image mode: the cached vector expired (stale/old link) → prompt a fresh upload, don't blank out.
          if (data && data.expired) { tzClearRevealTimer(); clearLoading(); clearMoreSpinner(); showExpired(); st.done = true; st.loading = false; return; }
          var products = (data && data.products) || [];
          if (firstFetch) {
            firstFetch = false;
            tzClearRevealTimer(); // we now own the DOM (results or empty state) — cancel the native-reveal safety timer
            if (!isCollection && !imageMode) beacon({ type: 'search', q: term, market: COUNTRY, resultCount: (data && data.total) || 0, anonId: anonId() });
            // Feed the base-set facets to the filter bar (page 1 only carries them). Before the empty check
            // so filters still render on a 0-result filter combo → the shopper can loosen them.
            // Facets/price come only on page 1's response. A ?page=N deep-link (crawler-oriented URL — humans
            // rarely land here: pagination links are hidden) starts mid-series with no facets → skip (never
            // pass null in). The bar self-populates the moment any filter is applied (that refetch → page 1).
            if (st.filters && data.facets) st.filters.setFacets(data.facets, data.priceMin, data.priceMax);
            // Keep the loading spinner up until the cards are actually rendered (below) — clearing it now
            // (on the BE response) leaves the grid empty for the ~1-2s the per-product Shopify card fetches
            // take, which reads as "no results". Only the empty-result path clears it here.
            if (!products.length) { clearLoading(); showEmpty(); st.done = true; st.loading = false; return; } // OUR empty state — not Shopify's fuzzy matches
          }
          st.page = next;
          st.total = data.total || 0;
          st.totalPages = data.totalPages || 1;
          // Cards render fully client-side from this JSON (card markup + F&F pricing built in JS) — ZERO
          // per-product Shopify calls. This replaced the old per-product Section-Rendering fetch path.
          return Promise.resolve(products.map(function (p) { return cardHtml(p); })).then(function (htmls) {
            clearLoading();       // cards are ready now → remove the initial full spinner (no empty flash)
            clearMoreSpinner();
            var heightBefore = document.documentElement.scrollHeight;
            var baseOffset = (next - 1) * PAGE_SIZE;
            htmls.forEach(function (html, i) {
              var p = products[i];
              var wrap = document.createElement('div');
              wrap.innerHTML = html;                      // client-built card (cardHtml) — includes F&F
              var el = wrap.firstElementChild;
              if (!el) return;
              // Shopify cards don't carry our click-tracking hooks — decorate the product links.
              var links = el.querySelectorAll('a[href*="/products/"]');
              for (var j = 0; j < links.length; j++) {
                links[j].setAttribute('data-tz-product-id', p.id);
                links[j].setAttribute('data-tz-position', baseOffset + i + 1);
              }
              st.grid.appendChild(el);
            });
            // Record this page's rendered height (aspect-ratio boxes give height without waiting on images)
            // → the infinite-scroll trigger uses it as a ONE-PAGE buffer: the next page loads as the shopper
            //   enters the current last page (reach page x-1 → load page x).
            var delta = document.documentElement.scrollHeight - heightBefore;
            if (delta > 0) st.lastPageHeight = delta;
            updateBar();
            renderJudgeme();
            st.loading = false;
            // Stop at the last page OR when a short/empty page comes back (also covers Meili's 1000-hit cap).
            if (products.length < PAGE_SIZE || st.page >= st.totalPages) st.done = true;
            // Restore mode: keep loading until the page is tall enough to reach the saved spot, THEN
            // jump there and hand off to the normal (distance-based) infinite scroll.
            if (restoring && !st.done && st.page < 15 &&
                document.documentElement.scrollHeight < savedY + (window.innerHeight || 600) + 300) {
              fetchPage(); return;
            }
            if (restoring) {
              restoring = false;
              requestAnimationFrame(function () { requestAnimationFrame(function () { window.scrollTo(0, savedY); }); });
              return;
            }
            // Normal mode: top up only if the loaded pages don't yet fill the viewport (self-stops).
            requestAnimationFrame(maybeLoad);
          });
        })
        .catch(function () {
          st.loading = false; clearMoreSpinner();
          if (st.page === 0) {
            // A single transient failure — classically the app RESUMING after minutes in the background with a
            // stale connection → the first fetch fails instantly — shouldn't tear the collection down to native.
            // Retry once after a short delay; only fall back (and count it against the circuit breaker) if the
            // retry ALSO fails. A successful retry calls cbReset() up top, so the breaker never trips on a blip.
            if ((st.firstRetries = (st.firstRetries || 0) + 1) <= 3) { setTimeout(fetchPage, 800 * st.firstRetries); }  // 0.8s,1.6s,2.4s — give a resuming network time to come back
            else { cbFail(); tzTeardown(); }   // still failing after retries → BE genuinely unreachable → native fallback
          } else { cbFail(); }                 // a later page failing just stops infinite scroll
        });
    }

    // Infinite scroll — load the next page ONLY when the viewport is within ~700px of the DOCUMENT
    // bottom (distance-based, not a sentinel that could sit mid-page → it never fires in the middle).
    // st.loading guards concurrency so it can't cascade; each 20-card page adds far more than 700px of
    // height, so one page load moves the bottom well out of range until the shopper scrolls again.
    function maybeLoad() {
      if (restoring || st.done || st.loading) return;
      var y = window.scrollY || window.pageYOffset || 0;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // Keep ONE page loaded ahead: trigger when the shopper is within one page-height of the bottom
      // (i.e. as they enter the current last page → load the next). Falls back to ~1.5 viewports until the
      // first page's height is measured. Still bottom-anchored, so it never fires mid-page.
      var buffer = st.lastPageHeight || (vh * 1.5);
      if (document.documentElement.scrollHeight - (y + vh) < buffer) fetchPage();
    }

    // Filter changed → re-run from page 1 with the new selection (module already updated the URL). Resets
    // pagination + grid, keeps the filter bar in place. Same base query is always included via resultsPath.
    function reloadWithFilters() {
      restoring = false;
      // A filter change re-runs from page 1, so this IS a "first fetch" again — reset the flag so the
      // first-fetch-only work runs: refresh the facet counts (setFacets) + the empty-state check. Without
      // this, changing filters left facet counts stale, skipped "no products found", and dropped the filter
      // options on clear (all needed a page reload).
      firstFetch = true;
      st.page = 0; st.done = false; st.loading = false; st.total = 0; st.totalPages = 1; st.lastPageHeight = 0;
      clearMoreSpinner();
      st.grid.innerHTML = ''; showLoading();
      window.scrollTo(0, 0);
      fetchPage();
    }

    function buildShell() {
      // Controls row: the "Filter" trigger (left) sits in PARALLEL with the Sort widget (right) — Globo-style.
      // Created first so the filter module drops its trigger into it (via triggerHost); main chips go below.
      st.controls = document.createElement('div'); st.controls.className = 'tz-controls';
      // Filter bar (reusable module) — the search query is the always-on "mega filter" scope (base=q=…).
      // Gated by FILTERS_ENABLED so one flag flip disables filters without touching the module.
      if (FILTERS_ENABLED && window.TaminzoFilters) {
        try {
          st.filterBar = document.createElement('div');
          st.filters = window.TaminzoFilters.create({
            api: API,
            mountEl: st.filterBar,
            market: COUNTRY,
            base: filterBase,
            path: ENDPOINT,                     // null → search endpoint; collection → /storefront/collection/results
            triggerHost: st.controls,           // Filter trigger lands in the controls row, beside Sort
            onChange: function () { reloadWithFilters(); }
          });
        } catch (e) { st.filters = null; st.filterBar = null; } // filter bug must never break results
      }
      // Sort widget — created here, but placed in the COUNT row (below), NOT the controls row, so it can't
      // collide with the now-full-width single-row filter bar. Appended to st.bar after it's built.
      if (st.filters && st.filters.renderSort) {
        st.sortSlot = document.createElement('div'); st.sortSlot.className = 'tz-sort-slot';
        try { st.filters.renderSort(st.sortSlot); } catch (e) {}
      }
      // Count row: "N Products" (left) + Sort (right). "Showing X of Y" dropped — redundant with infinite scroll.
      st.bar = document.createElement('div'); st.bar.className = 'tz-results-bar';
      st.countSpan = document.createElement('span'); st.countSpan.className = 'tz-results-count';
      st.bar.appendChild(st.countSpan);
      if (st.sortSlot) st.bar.appendChild(st.sortSlot);
      st.grid = document.createElement('div'); st.grid.className = 'spf-row tz-results';
      // Our OWN container carrying a duplicate id="gf-products": Globo's card CSS (scoped to
      // #gf-products) styles our cards exactly, but Globo's JS targets the first (original, hidden)
      // #gf-products, never ours → no fight, consistent for every query. .tz-gf marks it as ours.
      var mygf = document.createElement('div'); mygf.id = 'gf-products'; mygf.className = 'tz-gf';
      mygf.appendChild(st.grid);
      // page-width gives the standard theme side margins (matching collection pages). Our host
      // #ProductGridContainer has none, so the wrap must carry it (the native page-width #product-grid
      // is the hidden sibling we don't render into).
      var wrap = document.createElement('div'); wrap.className = 'tz-results-wrap page-width' + (isCollection ? ' tz-collection' : '');
      wrap.appendChild(st.controls);                      // [Filter | Sort] row (module inserts chips below it)
      if (st.filterBar) wrap.appendChild(st.filterBar);   // filter bar above the count + grid
      wrap.appendChild(st.bar); wrap.appendChild(mygf);
      container.appendChild(wrap);
      st.wrap = wrap;
      window.addEventListener('scroll', maybeLoad, { passive: true });
      window.addEventListener('resize', maybeLoad, { passive: true });
    }
    // "Loading more" spinner — appended at the END (below the loaded cards), never blocking the grid.
    function showMoreSpinner() {
      if (st.moreSpinner || !st.wrap) return;
      var d = document.createElement('div'); d.className = 'tz-loading-more';
      d.innerHTML = SPIN;
      st.wrap.appendChild(d);
      st.moreSpinner = d;
    }
    function clearMoreSpinner() {
      if (st.moreSpinner && st.moreSpinner.parentNode) st.moreSpinner.parentNode.removeChild(st.moreSpinner);
      st.moreSpinner = null;
    }
    function updateBar() {
      // Only update the count text — never innerHTML the bar (it holds the persistent Sort slot now).
      if (st.countSpan) st.countSpan.textContent = st.total + (st.total === 1 ? ' Product' : ' Products');
      if (st.filters && st.filters.setTotal) st.filters.setTotal(st.total);   // mobile drawer "View N Products"
    }
    function showLoading() { st.grid.innerHTML = '<div class="tz-results-loading">' + SPIN + '</div>'; }
    function clearLoading() { var l = st.grid.querySelector('.tz-results-loading'); if (l && l.parentNode) l.parentNode.removeChild(l); }
    function showEmpty() {
      if (st.bar) st.bar.innerHTML = '';
      var title = isCollection ? 'No products match your filters'
                : imageMode ? 'No visually-similar products found'
                            : 'No results for &ldquo;' + esc(term) + '&rdquo;';
      var sub = isCollection ? 'Try removing some filters to see more from this collection.'
              : imageMode ? 'We couldn&rsquo;t find products that look like your photo. Try another image, or explore our latest arrivals.'
                          : 'We couldn&rsquo;t find anything matching your search. Try a different keyword, or explore our latest arrivals.';
      st.grid.innerHTML =
        '<div class="tz-results-empty">' +
          '<span class="tz-empty-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>' +
          '</span>' +
          '<h2 class="tz-empty-title">' + title + '</h2>' +
          '<p class="tz-empty-sub">' + sub + '</p>' +
          (imageMode ? '<button type="button" class="button tz-empty-btn tz-reupload-btn">Search with another photo</button>'
                     : '<a href="/" class="button tz-empty-btn">Explore more products</a>') +
        '</div>';
    }
    // Image mode only: the cached vector expired (old/shared link) — we never stored the photo, so ask for a
    // fresh upload rather than showing a broken/empty grid.
    function showExpired() {
      if (st.bar) st.bar.innerHTML = '';
      st.grid.innerHTML =
        '<div class="tz-results-empty">' +
          '<span class="tz-empty-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"></path><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>' +
          '</span>' +
          '<h2 class="tz-empty-title">This visual search has expired</h2>' +
          '<p class="tz-empty-sub">Visual searches are kept for a limited time. Tap below to search with a photo again.</p>' +
          '<button type="button" class="button tz-empty-btn tz-reupload-btn">Search with a photo</button>' +
        '</div>';
    }
    // BE call failed on the first load → remove our takeover so native (Shopify) results show as fallback.
    function tzTeardown() {
      try { document.documentElement.classList.remove('tz-results-active'); } catch (e) {}
      tzClearRevealTimer();
      var w = document.querySelectorAll('.tz-results-wrap');
      for (var i = 0; i < w.length; i++) if (w[i].parentNode) w[i].parentNode.removeChild(w[i]);
    }

    // Hide the native/Shopify grid IMMEDIATELY — before the fetch — so there's no flash of Shopify's
    // page on submit and no Shopify fuzzy-match results on a no-match query (e.g. "heels"). Our grid,
    // loading spinner, or empty state renders in its place; only a failed BE call restores native.
    document.documentElement.classList.add('tz-results-active');
    buildShell();
    showLoading();
    fetchPage();
  }

  // Judge.me renders stars into .jdgm-preview-badge placeholders; re-trigger after we inject cards.
  function renderJudgeme() {
    try {
      if (window.jdgm) {
        if (typeof window.jdgm.batchSetPreviewBadges === 'function') window.jdgm.batchSetPreviewBadges();
        else if (typeof window.jdgm.customizeBadges === 'function') window.jdgm.customizeBadges();
      }
      if (typeof window.jdgmLoadPreviewBadges === 'function') window.jdgmLoadPreviewBadges();
    } catch (e) {}
  }

  // Rendered with GLOBO's exact card markup + classes (copied from its #gspfProduct template) so
  // Globo's own CSS — already loaded on the page — styles colours, fonts, badge spacing, image ratio
  // identically to the collection page. We only fill in our data + the Judge.me badge (its app JS
  // renders the stars). Images use a direct src (not Globo's lazyload) so they load in our grid.
  // ── F&F / Partner pricing (client-side) ─────────────────────────────────────
  // TEMPORARY bridge so search cards render fully client-side (no per-product Shopify Section fetch)
  // until Globo is retired. Tier lists are a verbatim MIRROR of snippets/friends-family-pricing.liquid
  // (card context) — if the Liquid tiers change, update BOTH. Handles are brand-collection handles; a
  // product's brand handle = handleize(cus_brand) (from /results brand_handle) or handleize(vendor).
  var FNF_TIERS = [
    [15, 'diamond,glitter-jewellery'],
    [10, 'saira-rizwan,edenrobe,scarves-by-madiha,ab-hand-embroideries,sapphire,sobia-nazir,johra,nishat,gul-ahmed,orient,saya,alkaram-studio'],
    [8,  'salaar-clothing,nourah,bagram-by-hamna,riwayat-wear,sara-jahan,anayra-amal,arshmans,6teen,pyara-jora,silai-libas,zebaish-collection,loyalies,dot-n-dot,rameens-collection,khubsurat-libas,ayat-closet,duas-boutique,libas-e-khas,farooq-hameed-studio,rubys-couture,gem-garments,islami-hijab-centre,tawakal-closet,shurooq,nabiha-studio,allure-by-ih,mh-cloth-house,zesh,almeerah,feroza,durwaj,zubia-motiwalla,tashu,rb-pret-couture,zeephire,mum-n-bub,shehroz-fashion,mappedpk,velvour,sitara-studio,rajbari,naqsh,zara-ali,sule,sitara-sapna,royale-leather,siraphina,paltar,masoori,danier,salonika,sana-safinaz,bin-faraz,h-a-jewellery,fatima-maryam,motifz,fatima-hussain,tehzeeb-libas,bin-shaukat,mausummery,sehrish-kanwal,shazif,fashion-hub-by-noor-bilal,joyels,maryam-hussain,stylish-collection,kachy-dhaagy,cross-sketch,noori,grace-style,elegant-bedding,imran-aftab,dynasty'],
    [5,  'chandan-nagri,hani-umaima,kashees,rabazka,lumira,hem,haleema-sadia,bunkara,manahil-arts,mak-fashion,anora,rang-rasiya,afrozeh,blush-bloom,savarna,irame,bin-rehman,lamorado,orya-jewellery,house-of-mahjan,coco-by-zara-shahjahan,zara-shahjahan,qalamkar,mushq,shiza-hassan,mannat-clothing,hemstitch,wearmani,panache-apparel,hussain-rehar,binilyas,parishay,jupiter,funky,fabrich,mahnur,musk,fehm,hu-studios,crimson'],
    [3,  'maria-b,zaha,elan,farah-talib-aziz,charizma,nureh,noor-by-saadia-asad,wardha-saleem,ansab-jahangir,suffuse-by-sana-yasir']
  ];
  // handle -> pct (15→3 order, first assignment wins — mirrors the Liquid loop's break precedence).
  var FNF_PCT = (function () {
    var m = {};
    for (var i = 0; i < FNF_TIERS.length; i++) {
      var pct = FNF_TIERS[i][0], hs = FNF_TIERS[i][1].split(',');
      for (var j = 0; j < hs.length; j++) if (!(hs[j] in m)) m[hs[j]] = pct;
    }
    return m;
  })();
  function handleize(s) {
    return String(s == null ? '' : s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  // Customer tags are exposed inline in theme.liquid (statsigConfig.user.custom.tags). Guests → [].
  function customerTags() {
    try { var u = window.statsigConfig && statsigConfig.user; return (u && u.custom && u.custom.tags) || []; }
    catch (e) { return []; }
  }
  function fnfLabel() {
    var t = customerTags();
    if (t.indexOf && t.indexOf('FnF') !== -1) return 'FnF Discount';
    if (t.indexOf && t.indexOf('Partner') !== -1) return 'Partner Discount';
    return '';
  }
  // Returns the F&F price block HTML (same markup/classes as the Liquid card snippet), or '' when the
  // shopper isn't F&F/Partner or the brand has no tier. Discount is applied to the (already uplifted)
  // base price, then formatPrice converts currency — matching how the card's normal price renders.
  function fnfHtml(p) {
    var label = fnfLabel();
    if (!label) return '';
    var handle = (p && p.brand_handle) || handleize(p && p.vendor);
    var pct = FNF_PCT[handle] || 0;
    if (!pct || p.price == null) return '';
    var final = p.price - (p.price * pct / 100);
    return '<div class="fnf-pricing fnf-card-pricing">' +
             '<span class="fnf-special-price">' + esc(formatPrice(final)) + '</span>' +
             '<span class="fnf-discount-badge">' + esc(label) + '</span>' +
           '</div>';
  }

  // Judge.me star rating, rendered CLIENT-SIDE from the numeric `rating` in /results (synced from the
  // reviews.rating metafield) — no Judge.me JS, no timing/batch issues. Gold overlay clipped to the
  // rating %, over a grey 5-star base (handles fractional ratings). Empty when there's no rating.
  // Render the SAME markup Judge.me uses on the PDP (jdgm-star jdgm--on/--half/--off + jdgm-prev-badge__text),
  // so Judge.me's own CSS (loaded via the app embed) styles it identically to the product page. We build it
  // ourselves from the numeric `rating` and mark it jdgm--done-setup so Judge.me's JS never re-processes it —
  // exact style match, zero runtime Judge.me calls. Unrated → 5 empty stars (consistent row), no number.
  function ratingStarsHtml(rating) {
    var r = typeof rating === 'number' ? rating : parseFloat(rating);
    if (!r || r < 0) r = 0;
    var stars = '';
    for (var i = 1; i <= 5; i++) {
      var cls = r >= i ? 'jdgm--on' : (r >= i - 0.5 ? 'jdgm--half' : 'jdgm--off');
      stars += '<span class="jdgm-star ' + cls + '"></span>';
    }
    return '<div class="jdgm-widget jdgm-preview-badge jdgm--done-setup" data-template="manual-installation" style="pointer-events:none">' +
        '<span class="jdgm-prev-badge__stars" role="img" aria-label="' + (r > 0 ? r.toFixed(1) + ' out of 5 stars' : 'No reviews yet') + '">' + stars + '</span>' +
        (r > 0 ? '<span class="jdgm-prev-badge__text" style="margin-left:3px">' + r.toFixed(1) + '</span>' : '') +
      '</div>';
  }

  function cardHtml(p) {
    var img = p.image || (p.featured_image && p.featured_image.url) || '';
    var onSale = p.compare_at_price != null && p.compare_at_price > p.price;
    var pct = onSale ? Math.round((p.compare_at_price - p.price) / p.compare_at_price * 100) : 0;
    var isNew = p.published_at && (Date.now() - new Date(p.published_at).getTime()) < NEW_DAYS * 86400000;
    var soldout = p.available === false;
    var url = esc(p.url);
    var priceInner = onSale
      ? '<span class="spf-product-card__oldprice money">' + esc(formatPrice(p.compare_at_price)) + '</span>' +
        '<span class="spf-product-card__saleprice money">' + esc(formatPrice(p.price)) + '</span>'
      : '<span class="spf-product-card__price money">' + esc(formatPrice(p.price)) + '</span>';
    return '<div class="spf-col-xl-3 spf-col-lg-4 spf-col-md-6 spf-col-sm-6 spf-col-6">' +
      '<div class="spf-product-card spf-product-card__left spf-product-card__template-5' + (soldout ? ' spf-soldout' : '') + ' card-wrapper product-card-wrapper underline-links-hover">' +
        '<div class="spf-product-card__inner">' +
          (soldout ? '<span class="spf-product__label spf-product__label-soldout">Sold out</span>' : '') +
          // A sold-out product is never "on sale" — suppress the sale % badge so it doesn't overlap "Sold out".
          (!soldout && pct > 0 ? '<span class="spf-product__label spf-product__label-sale sale-percent">-' + pct + '%</span>' : '') +
          (isNew ? '<span class="spf-product__label spf-product__label-new" style="background-color:#0aa90a;color:#fff;">New</span>' : '') +
          '<a class="spf-product-card__image-wrapper" href="' + url + '" style="padding-top:138.88888888889%;" data-tz-product-id="' + esc(p.id) + '">' +
            // Instant Shipping badge — same markup/classes as the collection card (styled by base.css)
            (p.instant_shipping ? '<div class="express-badge"><svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="fill-warning-300" weight="fill"><path d="M15.0858 8.30093C15.0538 8.1731 14.9366 8.09854 14.7662 8.09854H11.1017L13.7595 2.43675C13.8021 2.3462 13.8554 2.20772 13.7968 2.1012C13.7595 2.03728 13.6903 2 13.6051 2C13.4346 2 13.2961 2.14381 13.2429 2.19707L9.39733 6.2024L6.80878 8.88149L5.17363 10.5806C5.03515 10.7244 4.97656 10.8788 5.00852 11.012C5.04048 11.1451 5.15765 11.2197 5.32809 11.2197H8.40665H8.86471L8.68362 11.6245L5.99387 17.5686C5.95126 17.6591 5.90332 17.7923 5.96191 17.8988C5.9992 17.9627 6.06844 18 6.14833 18C6.3241 18 6.46791 17.8455 6.51052 17.7976L10.1909 13.8349L12.7369 11.0919L14.926 8.73236C15.0644 8.58322 15.1177 8.42876 15.0858 8.30093Z" fill="#FFB749"></path></svg><p class="express-badge-text">Instant Shipping</p></div>' : '') +
            // `lazyloaded` = Globo's own class → picks up its `#gf-products img.lazyloaded{height:100%;object-fit:cover}`
            // so the image fills the box exactly like collection cards (badge then sits flush).
            (img ? '<img class="spf-product-card__image spf-product-card__image-main lazyloaded" src="' + esc(imgUrl(img, 540)) + '" srcset="' + esc(imgSrcset(img)) + '" sizes="' + CARD_SIZES + '" alt="' + esc(p.title) + '" loading="lazy" decoding="async">' : '') +
          '</a>' +
          // Quick add — Globo's EXACT button markup + icon; our click drives the theme's own
          // <quick-add-modal> (same popup as the collection page). The .loading__spinner is required
          // by the theme's show() (it toggles it), and gives the identical native loading state.
          '<div class="quick-add no-js-hidden"><modal-opener class="spf-quickview">' +
            '<button type="button" class="quick-action__submit-icon quick-shop__submit-icon tz-quick-add" data-product-url="' + url + '" data-action="shop" aria-label="Quick add">' + QA_SVG +
              '<div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div>' +
            '</button>' +
          '</modal-opener></div>' +
        '</div>' +
        '<div class="spf-product__info">' +
          // Show the internal code "{brand} : {code}" (same as the Shopify card), not the raw title.
          '<div class="h4 spf-product-card__title"><a href="' + url + '" data-tz-product-id="' + esc(p.id) + '">' + esc(p.display_title || p.title) + '</a></div>' +
          ratingStarsHtml(p.rating) +
          '<div class="spf-product-card__price-wrapper">' + priceInner + fnfHtml(p) + '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  // Quick add — drive the theme's OWN <quick-add-modal> so the popup is byte-identical to the
  // collection page (fetches the product, native variant picker + add-to-cart + loading spinner).
  // We inject ONE standalone shell into <body> (the original card modals live inside the hidden
  // #gf-products grid → can't render; and typo/zero-native-result queries have none at all).
  var tzModal = null;
  function tzQuickModal() {
    if (tzModal && document.body.contains(tzModal)) return tzModal;
    if (!window.customElements || !customElements.get('quick-add-modal')) return null; // theme not ready
    var host = document.createElement('div');
    // Required by the theme's ModalDialog/QuickAddModal on upgrade: [id^="ModalClose-"] (close),
    // [role="dialog"] (focus trap), [id^="QuickAddInfo-"] (content target). Classes = quick-add.css.
    host.innerHTML =
      '<quick-add-modal id="QuickAdd-tzsearch" class="quick-add-modal">' +
        '<div role="dialog" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">' +
          '<button id="ModalClose-tzsearch" type="button" class="quick-add-modal__toggle" aria-label="Close">' +
            '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>' +
          '</button>' +
          '<div id="QuickAddInfo-tzsearch" class="quick-add-modal__content-info"></div>' +
        '</div>' +
      '</quick-add-modal>';
    tzModal = host.firstElementChild;
    document.body.appendChild(tzModal); // upgrade fires here; children already present
    return tzModal;
  }
  // Bind on WINDOW capture (not document) so we fire before Globo's document-level delegated click
  // handler — stopImmediatePropagation here blocks Globo from opening its own (different) popup.
  window.addEventListener('click', function (e) {
    try {
      var btn = e.target && e.target.closest && e.target.closest('.tz-quick-add');
      if (!btn) return;
      e.preventDefault(); e.stopImmediatePropagation();
      var modal = tzQuickModal();
      if (modal && typeof modal.show === 'function') { modal.show(btn); return; } // theme handles the spinner
      var purl = btn.getAttribute('data-product-url');
      if (purl) window.location.href = purl; // last resort — theme modal unavailable
    } catch (err) {}
  }, true);

  // Click tracking (dropdown + results grid).
  document.addEventListener('click', function (e) {
    try {
      var a = e.target && e.target.closest && e.target.closest('a[data-tz-product-id]');
      if (!a) return;
      beacon({ type: 'click', q: lastTerm || (new URLSearchParams(location.search).get('q') || ''),
        productId: parseInt(a.getAttribute('data-tz-product-id'), 10) || 0, position: parseInt(a.getAttribute('data-tz-position'), 10) || 0, anonId: anonId() });
    } catch (err) {}
  }, true);

  // ══ Search submit → CANCEL the native form submit and REDIRECT to /search?q=… ourselves. ══
  //    A clean full navigation. The /search page then loads NORMALLY: all its CSS applies, the
  //    predictive dropdown closes because it's a fresh page, the <head> guard (theme.liquid) hides the
  //    native grid before first paint (no Shopify flash), and initResultsPage() renders OUR DB results
  //    into the properly-styled container. We intercept the submit (rather than let the form go) only
  //    so Globo/theme JS can't hijack it — the destination is the same clean /search?q= URL.
  function runClientSearch(term) {
    term = (term || '').trim();
    if (!term) return;
    window.location.assign('/search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last');
  }

  // If a REAL suggestion (product/collection/brand link) is keyboard-highlighted, return its URL so we navigate
  // straight there. The "View all results" term option is NOT a real item → null → treat as a plain search.
  function tzSelectedItemUrl() {
    var a = document.querySelector('#predictive-search-results [aria-selected="true"] a, .predictive-search [aria-selected="true"] a');
    if (!a || a.classList.contains('tz-view-all') || a.closest('.predictive-search__search-for-button')) return null;
    var href = a.getAttribute('href');
    return href && href.charAt(0) !== '#' ? href : null;
  }
  // Handle Enter / search button / iOS "Search" OURSELVES and navigate immediately — so Dawn's predictive
  // component can never take its slow (~1-2s) native path on iOS. Blur first to dismiss the soft keyboard.
  function tzHandleSearchSubmit(input, e) {
    var term = (input && input.value || '').trim();
    if (!term) return;
    e.preventDefault(); e.stopImmediatePropagation();
    var itemUrl = tzSelectedItemUrl();
    try { if (input.blur) input.blur(); } catch (_) {}
    if (itemUrl) window.location.assign(itemUrl); else runClientSearch(term);
  }
  window.addEventListener('submit', function (e) {
    try {
      var form = e.target;
      if (!form || typeof form.querySelector !== 'function') return;
      var input = form.querySelector('input[name="q"]');
      if (input) tzHandleSearchSubmit(input, e);
    } catch (err) {}
  }, true);
  // Enter keydown — fires on iOS + Android soft keyboards; handled the same fast way (no deferring to native).
  window.addEventListener('keydown', function (e) {
    try {
      if (e.key !== 'Enter' && e.keyCode !== 13) return;
      var t = e.target;
      if (t && t.name === 'q') tzHandleSearchSubmit(t, e);
    } catch (err) {}
  }, true);

  // ── Mobile: keep the predictive dropdown OPEN when the soft keyboard is dismissed (blur), so users can
  //    scroll the suggestions. Dawn auto-closes on focusout; on touch we suppress that and close only via the
  //    ✕ button, a tap outside the search, or Enter/Search. ──
  var TZ_TOUCH = (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) || ('ontouchstart' in window);
  // Platform split for the image-search picker. Android (app + mobile web) needs our custom Take-Photo/Gallery
  // sheet: the app can't return a photo from the WebView file-input's camera, and Android Chrome's plain file
  // input lands in the gallery and never offers the camera. iOS (app + web) and desktop DON'T need the sheet —
  // a plain <input type=file accept="image/*"> already shows iOS's native "Take Photo / Photo Library" menu
  // (WKWebView handles it in the app too), and desktop just gets the file dialog. Only Android gets the sheet.
  var TZ_UA = (navigator && navigator.userAgent) || '';
  var IS_ANDROID = (window.isTaminzoApp === 'android') || /Android/.test(TZ_UA);
  function tzSetupMobilePredictive() {
    if (!TZ_TOUCH) return;
    var list = document.querySelectorAll('predictive-search');
    for (var i = 0; i < list.length; i++) {
      var ps = list[i];
      if (ps.__tzMobile) continue; ps.__tzMobile = true;
      // Capture focusout BEFORE Dawn's (bubble) listener on the element → stop it so blur doesn't auto-close.
      ps.addEventListener('focusout', function (e) { e.stopImmediatePropagation(); }, true);
    }
  }
  if (TZ_TOUCH) {
    // ✕ button inside the dropdown → close it.
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.tz-ps-close');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      var ps = btn.closest('predictive-search');
      try { if (ps && typeof ps.close === 'function') ps.close(); } catch (_) {}
    }, true);
    // Tap outside the search → close (we disabled the blur auto-close above).
    document.addEventListener('pointerdown', function (e) {
      var open = document.querySelectorAll('predictive-search[open]');
      for (var i = 0; i < open.length; i++) {
        if (!open[i].contains(e.target)) { try { open[i].close(); } catch (_) {} }
      }
    }, true);
  }

  // ══ IMAGE (VISUAL) SEARCH — camera button → resize → upload → /search?img={id} ══
  // Runs in mobile web AND inside the app WebView (plain file input, no native bridge). We NEVER keep the
  // photo — it's embedded server-side into a vector and discarded; only a short id comes back.
  var tzImageSearch = (function () {
    var fileInput = null;

    function overlay(on) {
      var o = document.getElementById('tz-img-overlay');
      if (on) {
        if (o) return;
        o = document.createElement('div'); o.id = 'tz-img-overlay';
        o.innerHTML = '<div class="tz-img-overlay-box">' + SPIN +
          '<p>Finding similar products&hellip;</p></div>';
        document.body.appendChild(o);
      } else if (o && o.parentNode) { o.parentNode.removeChild(o); }
    }

    // POST a ready blob (already cropped + sized by the cropper) → get an id → go to the results page.
    function uploadBlob(blob) {
      overlay(true);
      var form = new FormData();
      form.append('image', blob, 'search.jpg');
      origFetch(API + '/storefront/search/image?market=' + encodeURIComponent(COUNTRY),
        { method: 'POST', body: form, mode: 'cors', credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (data) {
          if (data && data.id) { location.href = '/search?img=' + encodeURIComponent(data.id); return; } // navigates away
          overlay(false);
          alert(data && data.message ? data.message : 'Sorry, image search is unavailable right now. Please try again.');
        })
        .catch(function () { overlay(false); alert('Couldn’t search that image. Please try another photo.'); });
    }

    // Crop step: let the shopper box just the garment before searching (less background/model noise → better
    // match). Full-screen overlay with a draggable/resizable crop box (touch + mouse); the box defaults to the
    // WHOLE image, so tapping Search without adjusting searches the full photo. On Search we draw only the
    // selected region to a canvas (≤900px, EXIF orientation handled by drawing from the <img>) and upload that.
    function openCropper(file) {
      try { console.log('TZCAM openCropper: type=' + (file && file.type) + ' size=' + (file && file.size)); } catch (e) {}
      var url = URL.createObjectURL(file);
      var host = document.createElement('div');
      host.id = 'tz-crop';
      host.innerHTML =
        '<div class="tz-crop-bar">' +
          '<button type="button" class="tz-crop-x">Cancel</button>' +
          '<span class="tz-crop-title">Crop close to the item</span>' +
          '<button type="button" class="tz-crop-go">Search</button>' +
        '</div>' +
        '<div class="tz-crop-stage">' +
          '<img class="tz-crop-img" alt="">' +
          '<div class="tz-crop-box"><i class="tz-h tl"></i><i class="tz-h tr"></i><i class="tz-h bl"></i><i class="tz-h br"></i></div>' +
        '</div>';
      document.body.appendChild(host);
      var prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';   // lock page scroll behind the modal

      var img = host.querySelector('.tz-crop-img');
      var box = host.querySelector('.tz-crop-box');
      function close() {
        try { URL.revokeObjectURL(url); } catch (e) {}
        document.documentElement.style.overflow = prevOverflow;
        if (host.parentNode) host.parentNode.removeChild(host);   // removing host drops its listeners too
        revealHeader();
      }
      host.querySelector('.tz-crop-x').addEventListener('click', close);

      img.onload = function () {
        function imgBox() { return { l: img.offsetLeft, t: img.offsetTop, w: img.offsetWidth, h: img.offsetHeight }; }
        // Open the crop box INSET (not the full image): the corner handles then sit inside the frame — easy to
        // grab instead of jammed against the screen edge — and it nudges toward a tighter garment crop, which
        // is what surfaces the exact product at the top of results.
        var ib0 = imgBox(), INSET = 0.15;
        var b = { l: ib0.l + ib0.w * INSET, t: ib0.t + ib0.h * INSET, w: ib0.w * (1 - 2 * INSET), h: ib0.h * (1 - 2 * INSET) };
        function apply() { box.style.left = b.l + 'px'; box.style.top = b.t + 'px'; box.style.width = b.w + 'px'; box.style.height = b.h + 'px'; }
        function clamp() {
          var ib = imgBox(), MIN = 36;
          if (b.w < MIN) b.w = MIN; if (b.h < MIN) b.h = MIN;
          if (b.w > ib.w) b.w = ib.w; if (b.h > ib.h) b.h = ib.h;
          if (b.l < ib.l) b.l = ib.l; if (b.t < ib.t) b.t = ib.t;
          if (b.l + b.w > ib.l + ib.w) b.l = ib.l + ib.w - b.w;
          if (b.t + b.h > ib.t + ib.h) b.t = ib.t + ib.h - b.h;
        }
        apply();

        var drag = null;
        function pt(e) { var t = e.touches && e.touches[0]; return { x: t ? t.clientX : e.clientX, y: t ? t.clientY : e.clientY }; }
        function onDown(e) {
          var el = e.target, corner = (el.classList && el.classList.contains('tz-h')) ? el.className.replace('tz-h', '').trim() : null;
          var p = pt(e);
          drag = { corner: corner, x: p.x, y: p.y, s: { l: b.l, t: b.t, w: b.w, h: b.h } };
          e.preventDefault();
        }
        function onMove(e) {
          if (!drag) return;
          var p = pt(e), dx = p.x - drag.x, dy = p.y - drag.y, s = drag.s, c = drag.corner;
          if (!c) { b.l = s.l + dx; b.t = s.t + dy; }
          else {
            if (c.indexOf('l') >= 0) { b.l = s.l + dx; b.w = s.w - dx; }
            if (c.indexOf('r') >= 0) { b.w = s.w + dx; }
            if (c.indexOf('t') >= 0) { b.t = s.t + dy; b.h = s.h - dy; }
            if (c.indexOf('b') >= 0) { b.h = s.h + dy; }
          }
          clamp(); apply(); e.preventDefault();
        }
        function onUp() { drag = null; }
        box.addEventListener('mousedown', onDown);
        box.addEventListener('touchstart', onDown, { passive: false });
        host.addEventListener('mousemove', onMove);
        host.addEventListener('touchmove', onMove, { passive: false });
        host.addEventListener('mouseup', onUp);
        host.addEventListener('touchend', onUp);

        host.querySelector('.tz-crop-go').addEventListener('click', function () {
          var ir = img.getBoundingClientRect(), br = box.getBoundingClientRect();
          var sx = img.naturalWidth / ir.width, sy = img.naturalHeight / ir.height;
          var cx = Math.max(0, (br.left - ir.left) * sx), cy = Math.max(0, (br.top - ir.top) * sy);
          var cw = Math.min(img.naturalWidth - cx, br.width * sx), ch = Math.min(img.naturalHeight - cy, br.height * sy);
          if (cw < 1 || ch < 1) { alert('Please select an area to search.'); return; }
          var maxDim = 900, sc = Math.min(1, maxDim / Math.max(cw, ch));
          var cv = document.createElement('canvas');
          cv.width = Math.max(1, Math.round(cw * sc)); cv.height = Math.max(1, Math.round(ch * sc));
          try { cv.getContext('2d').drawImage(img, cx, cy, cw, ch, 0, 0, cv.width, cv.height); }
          catch (e) { alert('Couldn’t process that image. Please try another.'); return; }
          cv.toBlob(function (blob) {
            if (!blob) { alert('Couldn’t process that image. Please try another.'); return; }
            close(); uploadBlob(blob);
          }, 'image/jpeg', 0.85);
        });
      };
      img.onerror = function () { try { console.log('TZCAM img ERROR'); } catch (e) {} close(); alert('Couldn’t open that image. Please try another.'); };
      var _ol = img.onload; img.onload = function () { try { console.log('TZCAM img loaded ' + img.naturalWidth + 'x' + img.naturalHeight); } catch (e) {} return _ol.apply(this, arguments); };
      img.src = url;
    }

    // File input wired to run the search on pick. Reused for the results-page re-upload button (which lives
    // OUTSIDE any modal, so a body-level input is fine there).
    function newFileInput() {
      var fi = document.createElement('input');
      fi.type = 'file';
      fi.accept = 'image/*';   // NO `capture` → the OS offers BOTH "Take Photo" and "Photo Library"
      fi.className = 'tz-cam-input';
      fi.addEventListener('change', function () {
        var f = fi.files && fi.files[0];
        try { console.log('TZCAM change: files=' + (fi.files ? fi.files.length : 'null') + ' name=' + (f ? f.name : '-') + ' size=' + (f ? f.size : '-')); } catch (e) {}
        fi.value = '';          // reset so picking the same file again re-fires change
        if (f) openCropper(f);
      });
      return fi;
    }
    function ensureInput() {
      if (fileInput) return fileInput;
      fileInput = newFileInput();
      document.body.appendChild(fileInput);
      return fileInput;
    }
    function pick() { try { ensureInput().click(); } catch (e) {} }

    var CAM_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>';

    // ── Native app bridge ──────────────────────────────────────────────────────────────────────
    // In the Taminzo app (flutter_inappwebview), the WebView's own file-input CAMERA capture doesn't return
    // the photo on Android — so we call the native picker instead (handler `tzPickImage`) and feed the
    // returned base64 straight into the crop UI. On the web there's no bridge → we keep the file input.
    function inApp() { return !!(window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === 'function'); }
    function nativePick(source) {
      try {
        window.flutter_inappwebview.callHandler('tzPickImage', source).then(function (dataUrl) {
          if (!dataUrl) return;   // user cancelled
          fetch(dataUrl).then(function (r) { return r.blob(); }).then(function (blob) {
            var file; try { file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' }); } catch (e) { file = blob; }
            openCropper(file);   // openCropper only needs createObjectURL — a Blob works too
          }).catch(function () {});
        }).catch(function () {});
      } catch (e) {}
    }
    // Web (mobile browser): open a file input. Android Chrome's plain file input often lands straight in the
    // gallery and never offers the camera — so for "camera" we set capture=environment to FORCE the camera.
    function webPick(source) {
      var fi = newFileInput();
      if (source === 'camera') fi.setAttribute('capture', 'environment');
      fi.style.display = 'none';
      document.body.appendChild(fi);
      try { fi.click(); } catch (e) {}
    }
    function chosen(source) { if (inApp()) nativePick(source); else webPick(source); }
    // Our full-screen overlays can leave Dawn's sticky header in a bad state:
    //  1. (non-always-sticky) it hides on the scroll our overlay triggers and `preventReveal` blocks the reveal.
    //  2. the "full search bar" is a <details-modal><details open> — when our overlay steals focus that <details>
    //     loses its `open` attribute and the whole bar COLLAPSES to just the search icon (header row stays put,
    //     so it reads as "header there, search bar gone" and only comes back on refresh, which re-renders `open`).
    // Force both back: clear the hidden class + preventReveal flag, and re-assert `open` on the always-open bars.
    function revealHeader() {
      try {
        var sh = document.querySelector('sticky-header'); if (sh) sh.preventReveal = false;
        var sec = document.querySelector('.section-header');
        if (sec) sec.classList.remove('shopify-section-header-hidden', 'scrolled-past-header');
        var bars = document.querySelectorAll('.mobile-search details-modal > details, .desktop-search details-modal > details');
        for (var i = 0; i < bars.length; i++) bars[i].setAttribute('open', '');
      } catch (e) {}
    }
    // Bottom-sheet to choose camera vs gallery — shown in the app AND on mobile web (both take ONE source).
    function pickSheet() {
      var o = document.createElement('div'); o.className = 'tz-src-sheet';
      o.innerHTML = '<div class="tz-src-box">' +
        '<div class="tz-src-head">' +
          '<span class="tz-src-title">Search by image</span>' +
          '<button type="button" data-src="cancel" class="tz-src-x" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>' +
          '</button>' +
        '</div>' +
        '<button type="button" data-src="camera">Take Photo</button>' +
        '<button type="button" data-src="gallery">Choose from Gallery</button>' +
        '</div>';
      // Freeze the background scroll while the sheet is up — otherwise the list shifts and Dawn's sticky
      // header hides (and doesn't come back). Same technique the crop overlay uses.
      var prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      function done(s) {
        document.documentElement.style.overflow = prevOverflow;
        if (o.parentNode) o.parentNode.removeChild(o);
        revealHeader();
        if (s === 'camera' || s === 'gallery') chosen(s);
      }
      o.addEventListener('click', function (e) {
        if (e.target === o) { done(null); return; }
        var btn = e.target.closest && e.target.closest('button'); if (!btn) return;
        done(btn.getAttribute('data-src'));
      });
      o.addEventListener('touchmove', function (e) { e.preventDefault(); }, { passive: false }); // no scroll-through
      document.body.appendChild(o);
    }
    // Unified entry. Any in-app WebView (Android OR iOS) + Android mobile web → the Take Photo/Gallery sheet,
    // so the app routes through the native picker and can check the camera permission BEFORE opening it (no
    // black-camera on denial). iOS Safari + desktop → plain file input (the OS handles the picker + denial).
    function startPick() { if (IS_ANDROID || inApp()) pickSheet(); else pick(); }

    // The camera control: in-app it's a plain button → native picker; on web a <label> with a nested file
    // input (opens the OS picker natively, keeping focus in the search modal so it doesn't focusout-close).
    function decorate(inp) {
      if (!IMAGE_SEARCH_ENABLED) return;   // feature flag — no camera button when off
      if (!inp || inp.__tzCam) return;
      // ONLY the real, visible search field. The facets/filter forms carry hidden name="q" inputs; decorating
      // those drops a stray, unpositioned label into the flow → widens the bar → horizontal scroll. Require a
      // .search wrapper (where the absolute-positioning CSS applies) and skip hidden inputs.
      if (inp.type === 'hidden' || !inp.closest || !inp.closest('.search')) return;
      inp.__tzCam = true;
      var el;
      if (IS_ANDROID || inApp()) {
        // In-app (Android OR iOS) + Android mobile web: a button that opens our Take Photo / Gallery sheet. The
        // app routes through the native picker so it can check the camera permission first (no black-camera on
        // denial + a Settings prompt); Android Chrome needs `capture` to reach the camera at all.
        el = document.createElement('button');
        el.type = 'button'; el.className = 'tz-cam-btn';
        el.setAttribute('aria-label', 'Search by image'); el.title = 'Search by image';
        el.innerHTML = CAM_SVG;
        el.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); pickSheet(); });
      } else {
        // iOS Safari + desktop: a <label> with a nested file input. iOS shows its native "Take Photo / Photo
        // Library" menu (Safari also handles a denied camera gracefully); desktop gets the file dialog. No sheet.
        // The nested input keeps focus inside the search modal so Dawn doesn't focusout-close it.
        el = document.createElement('label');
        el.className = 'tz-cam-btn';
        el.setAttribute('aria-label', 'Search by image'); el.title = 'Search by image';
        el.innerHTML = CAM_SVG;
        el.appendChild(newFileInput());
        el.addEventListener('click', function (e) { e.stopPropagation(); });   // don't bubble to modal outside-close
      }
      try { inp.insertAdjacentElement('afterend', el); } catch (e) {}
    }
    function setup() {
      var inputs = document.querySelectorAll('input[name="q"]:not([type="hidden"])');
      for (var i = 0; i < inputs.length; i++) decorate(inputs[i]);
    }

    // Re-upload buttons in the empty / expired states — native picker in-app, file input on web.
    document.addEventListener('click', function (e) {
      var b = e.target && e.target.closest && e.target.closest('.tz-reupload-btn');
      if (b) { e.preventDefault(); startPick(); }
    });
    // Header/mobile search inputs can mount lazily (Dawn opens the search in a modal) → decorate on focus too.
    document.addEventListener('focusin', function (e) {
      if (e.target && e.target.name === 'q') decorate(e.target);
    }, true);

    return { setup: setup, pick: pick };
  })();

  // Collection page: take over ONLY when enabled AND the theme injected the handle (theme.liquid gates the
  // injection on request.page_type == 'collection' + the theme-setting kill-switch). Runs the SAME results
  // engine as search, scoped to the collection with the collection endpoint.
  function initCollectionPage() {
    if (!COLLECTION_ENABLED) return;
    var col = window.taminzoCollection;
    if (!col || !col.handle) return;   // not a collection page, or takeover disabled in the theme setting
    initResultsPage(col);
  }

  function tzInit() { initResultsPage(); initCollectionPage(); tzSetupMobilePredictive(); tzImageSearch.setup(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tzInit);
  else tzInit();
})();
